<script setup>
import { defineProps, computed, reactive } from 'vue';
import Message from './Message.vue';
import NoteReads from './NoteReads.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

// Agentes que han participado en la conversación (escribieron algún mensaje o
// nota). Audiencia del "leído" de las notas internas · sin importar sucursal.
const noteParticipants = computed(() => {
  const seen = new Map();
  allMessages.value.forEach(m => {
    const s = m.sender;
    if (s?.id && (s.type || '').toLowerCase() === 'user' && !seen.has(s.id)) {
      seen.set(s.id, { id: s.id, name: s.availableName || s.name });
    }
  });
  return [...seen.values()];
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

// Separador de fecha entre días (Victor 2026-06-11 · Propuesta A): se inserta
// antes del primer mensaje de cada día. Orden cronológico (lo más viejo arriba,
// Hoy abajo). Formato natural en español:
//   Hoy · Ayer · día de semana si es de esta semana ("Lunes 8 de junio") ·
//   "d de mes" si es del mismo año · "d de mes de AAAA" si es de otro año.
const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const capitalize = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const formatDateSeparator = epoch => {
  const d = new Date(epoch * 1000);
  const now = new Date();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (diffDays <= 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  const day = d.getDate();
  const month = d.toLocaleDateString('es-DO', { month: 'long' });
  if (diffDays < 7) {
    const weekday = capitalize(d.toLocaleDateString('es-DO', { weekday: 'long' }));
    return `${weekday} ${day} de ${month}`;
  }
  if (d.getFullYear() === now.getFullYear()) return `${day} de ${month}`;
  return `${day} de ${month} de ${d.getFullYear()}`;
};

// Etiqueta del separador si este mensaje abre un nuevo día (o es el primero).
const dateSeparatorFor = index => {
  const list = allMessages.value;
  const current = list[index];
  if (!current?.createdAt) return null;
  if (index === 0) return formatDateSeparator(current.createdAt);
  const prev = list[index - 1];
  const a = new Date(current.createdAt * 1000);
  const b = new Date((prev?.createdAt || 0) * 1000);
  const sameDay =
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  return sameDay ? null : formatDateSeparator(current.createdAt);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <li
        v-if="dateSeparatorFor(index)"
        class="flex items-center gap-3 my-3 select-none list-none"
      >
        <span class="flex-1 h-px bg-n-weak" />
        <span
          class="text-xs font-medium text-n-slate-11 bg-n-solid-2 border border-n-weak rounded-full px-3.5 py-1 whitespace-nowrap"
        >{{ dateSeparatorFor(index) }}</span>
        <span class="flex-1 h-px bg-n-weak" />
      </li>
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
      <NoteReads
        v-if="message.private"
        :message="message"
        :participants="noteParticipants"
        :current-user-id="currentUserId"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
